import { isUserLoggedIn } from '@abovo/shared-auth';
import { GROUPING_TOUR_ID, TIME_REGISTRATION_TOUR_ID } from '@abovo/shared-services';
import { TOUR_REGISTRY } from '@abovo/shared-ui-components';
import { EnvironmentProviders, inject, Injector, provideAppInitializer, Type } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, firstValueFrom } from 'rxjs';
import { GroupingTourService } from '../features/filterbar/tour/grouping-tour.service';
import { TimeRegistrationTourService } from '../features/time-registration/tour/time-registration-tour.service';

/**
 * The service implementing each registered tour. `TOUR_REGISTRY` decides which tours exist; this only
 * says what runs them, and is separate because the registry lives in a UI library that may not import the
 * feature layer. Keep the two in step — a tour registered without an entry here is offered in the
 * shortcut panel but never responds, so the lookup below reports that instead of skipping it quietly.
 */
const TOUR_SERVICES: Record<string, Type<unknown>> = {
   [TIME_REGISTRATION_TOUR_ID]: TimeRegistrationTourService,
   [GROUPING_TOUR_ID]: GroupingTourService,
};

/**
 * Brings the guided-tour services to life once the user is logged in. Merely constructing one wires up
 * its "Take tour" launcher subscription and its once-per-device auto-start, which then waits reactively
 * for whatever gates it (an ACL permission, a filterbar offering grouping). Nothing else injects these
 * services, so without this they would never exist and no tour could be started.
 *
 * Deferring to login keeps the tours and their feature dependencies out of the app's startup path.
 */
export function provideTours(): EnvironmentProviders {
   return provideAppInitializer(() => {
      const store = inject(Store);
      const injector = inject(Injector);

      const tourServices = TOUR_REGISTRY.map((tour) => {
         const tourService = TOUR_SERVICES[tour.id];
         if (!tourService) {
            console.error(`Tour "${tour.id}" is registered but has no service in TOUR_SERVICES, so it can never start.`);
         }
         return tourService ?? null;
      }).filter((tourService): tourService is Type<unknown> => tourService !== null);

      void firstValueFrom(store.select(isUserLoggedIn).pipe(filter(Boolean))).then(() => tourServices.forEach((tourService) => injector.get(tourService)));
   });
}
